import React, { useCallback, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { getAuth, signOut } from "firebase/auth";
import { useTranslation } from "react-i18next";

import { firebaseApp } from "../../firebase/firebaseApp";
import BrandLogo from "../branding/BrandLogo";
import { LogoutIcon } from "../icons/LogoutIcon";
import { useAuth } from "../../context/AuthContext";
import { useWording } from "../../hooks/useWording";
import { AdminIcon } from "../icons/AdminIcon";
import {
  canManageDashboardOrganisation,
  canViewDashboardPatientData,
  canViewDashboardStudies,
} from "../../utils/authentication/dashboardPermissions";
import { LANGS } from "../../utils/constants";

/**
 * @param {object} props
 * @param {string=} props.className Extra Tailwind classes for the `<header>`.
 * @returns {JSX.Element}
 */
function NavBar({ className = "" }) {
  const { t, i18n } = useTranslation();
  const { user, claims, accountContext, organisationLoading, isBootstrapAdmin } =
    useAuth();
  const { subjects, sites } = useWording();

  const navigate = useNavigate();
  const location = useLocation();

  const permissionBridge = {
    claims,
    email: user?.email,
    isBootstrapAdmin,
  };
  const isAdmin = canManageDashboardOrganisation(
    accountContext,
    permissionBridge,
  );
  const canViewPatients =
    canViewDashboardPatientData(accountContext, permissionBridge) &&
    !organisationLoading;

  const canViewStudies =
    canViewDashboardStudies(accountContext, permissionBridge) &&
    !organisationLoading;
  const primaryNavPath = canViewPatients
    ? "/patients"
    : canViewStudies
      ? "/studies"
      : isAdmin
        ? "/admin"
        : "/patients";
  const primaryNavItemCount = Number(canViewPatients) + Number(canViewStudies);
  const isPatientsRoute =
    canViewPatients && location.pathname.startsWith("/patients");
  const isStudiesRoute = canViewStudies && location.pathname.startsWith("/studies");
  const showPrimaryNavActive = isPatientsRoute || isStudiesRoute;

  const handleSignOut = useCallback(async () => {
    try {
      await signOut(getAuth(firebaseApp));
      navigate("/auth/login", { replace: true });
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.error(error);
      }
    }
  }, [navigate]);

  const [openLangSwitcher, setOpenLangSwitcher] = useState(false);

  const current =
    LANGS.find((l) => l.code === i18n.resolvedLanguage) || LANGS[0];

  /** Change language and close pop-up */
  const choose = (code) => {
    i18n.changeLanguage(code);
    setOpenLangSwitcher(false);
  };

  const showLabelWhenClosed = false;

  return (
    <header
      className={`relative z-50 flex w-full items-center justify-between px-4 pt-2 md:min-h-16  ${className}`}
    >
      {/* Left: logo */}
      <Link
        to={primaryNavPath}
        aria-label={t("navBar.home", "PDX Home")}
        className="relative z-10 shrink-0"
      >
        <BrandLogo
          alt="PDX logo"
          className="h-10 w-auto object-contain"
        />
      </Link>

      {/* Centre: sliding navigation */}
      {primaryNavItemCount > 0 ? (
        <nav
          aria-label={t("navBar.mainNavigation", "Main navigation")}
          className="absolute left-1/2 top-full mt-2 -translate-x-1/2 md:top-1/2 md:mt-0 md:-translate-y-1/2"
        >
          <div
            className={`relative grid h-11 items-stretch p-1 card ${
              primaryNavItemCount > 1 ? "grid-cols-2" : "grid-cols-1"
            }`}
          >
            <span
              data-testid="nav-active-pill"
              aria-hidden="true"
              className={`pointer-events-none absolute bottom-1 top-1 rounded-xl transition-all duration-300 ease-in-out ${
                showPrimaryNavActive
                  ? "bg-primary opacity-100 shadow-sm"
                  : "opacity-0"
              } ${
                primaryNavItemCount > 1
                  ? "left-1 w-[calc(50%-0.25rem)]"
                  : "left-1 right-1"
              } ${
                primaryNavItemCount > 1 && isStudiesRoute
                  ? "translate-x-full"
                  : "translate-x-0"
              }`}
            />

            {canViewPatients ? (
              <NavLink
                to="/patients"
                className={({ isActive }) =>
                  `relative z-10 flex h-full min-w-36 items-center justify-center whitespace-nowrap rounded-lg px-4 text-center text-sm font-semibold transition-colors duration-300 ${
                    isActive
                      ? "text-greyscale-1"
                      : "text-primary hover:text-primary"
                  }`
                }
              >
                {subjects}
              </NavLink>
            ) : null}

            {canViewStudies ? (
              <NavLink
                to="/studies"
                className={({ isActive }) =>
                  `relative z-10 flex h-full min-w-36 items-center justify-center whitespace-nowrap rounded-lg px-4 text-center text-sm font-semibold transition-colors duration-300 ${
                    isActive
                      ? "text-greyscale-1"
                      : "text-primary hover:text-primary"
                  }`
                }
              >
                {sites}
              </NavLink>
            ) : null}
          </div>
        </nav>
      ) : null}

      {/* Right: actions */}
      <div className="relative z-10 ml-auto flex shrink-0 gap-2 items-stretch">
        {/* <LangSwitcher /> */}

        {isAdmin && (
          <NavLink
            to="/admin"
            aria-label={t("navBar.admin", "Admin")}
            className={({ isActive }) =>
              [
                "flex min-w-[44px] items-center justify-center whitespace-nowrap",
                "rounded-2xl px-4 py-2 text-center text-sm shadow-sm",
                "transition-colors duration-300",
                isActive ? "bg-primary" : "bg-container hover:bg-greyscale-1",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <AdminIcon
                className={[
                  "h-6 w-auto cursor-pointer transition-colors duration-300",
                  isActive ? "fill-greyscale-1" : "fill-tertiary",
                ].join(" ")}
              />
            )}
          </NavLink>
        )}

        <div className="relative flex items-stretch">
          <button
            type="button"
            onClick={() => setOpenLangSwitcher((open) => !open)}
            className={
              " flex min-w-[44px] items-center justify-center whitespace-nowrap rounded-2xl bg-container px-4 py-2 text-center text-sm shadow-sm transition-colors duration-300 hover:bg-greyscale-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            }
            aria-label={t("navBar.changeLanguage", "Change language")}
            aria-haspopup="menu"
            aria-expanded={openLangSwitcher}
          >
            <span
              aria-hidden="true"
              className="flex h-6 w-6 items-center justify-center text-xl leading-none"
            >
              {current.flag}
            </span>

            {showLabelWhenClosed ? (
              <span className="ml-2">{current.label}</span>
            ) : null}
          </button>

          {openLangSwitcher ? (
            <ul
              role="menu"
              aria-label={t("navBar.languageOptions", "Language options")}
              className="
        absolute right-0 top-full z-50 mt-2
        flex min-w-44 flex-col overflow-hidden
        rounded-2xl bg-container p-1 shadow-lg
      "
            >
              {LANGS.map(({ code, label, flag }) => {
                const isSelected = code === current.code;

                return (
                  <li key={code} role="none">
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={isSelected}
                      onClick={() => choose(code)}
                      className={[
                        "flex w-full items-center gap-3 rounded-xl px-3 py-2",
                        "text-left text-sm transition-colors duration-200",
                        "focus-visible:outline-none focus-visible:ring-2",
                        "focus-visible:ring-primary",
                        isSelected
                          ? "bg-primary text-greyscale-1"
                          : "text-typography hover:bg-greyscale-1",
                      ].join(" ")}
                    >
                      <span
                        aria-hidden="true"
                        className="flex h-6 w-6 shrink-0 items-center justify-center text-xl leading-none"
                      >
                        {flag}
                      </span>

                      <span className="flex-1 whitespace-nowrap">{label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>

        <NavLink
          to="/about"
          aria-label={t("navBar.about", "About")}
          className={({ isActive }) =>
            [
              "flex min-w-[44px] items-center justify-center whitespace-nowrap",
              "rounded-2xl px-4 py-2 text-center text-sm shadow-sm",
              "transition-colors duration-300",
              isActive ? "bg-primary" : "bg-container hover:bg-greyscale-1",
            ].join(" ")
          }
        >
          {({ isActive }) => (
            <span
              className={[
                "material-symbols-outlined text-xl leading-none transition-colors duration-300",
                isActive ? "text-greyscale-1" : "text-tertiary",
              ].join(" ")}
              aria-hidden="true"
            >
              info
            </span>
          )}
        </NavLink>

        <button
          type="button"
          onClick={handleSignOut}
          aria-label={t("navBar.signOut", "Sign out")}
          className="
            flex min-w-[44px] items-center justify-center whitespace-nowrap
            rounded-2xl bg-container px-4 py-2 text-center text-sm shadow-sm
            transition-colors duration-300 hover:bg-greyscale-1
          "
        >
          <LogoutIcon className="h-6 w-auto cursor-pointer fill-tertiary" />
        </button>
      </div>
    </header>
  );
}

export default React.memo(NavBar);
